import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  Download,
  LayoutDashboard,
  LayoutGrid,
  KeyRound,
  Link2,
  Menu,
  Pill,
  Settings,
  ShoppingCart,
  Wifi,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import { cn } from '@/lib/cn';
import { Brand } from './Brand';
import { EnvironmentBadge } from './EnvironmentBadge';
import { UserMenu } from './UserMenu';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  permission: Permission;
  end?: boolean;
};

type CategoryContext = {
  code: 'PHARMACY' | 'SUPERMARKET';
  label: string;
  listPath: string;
  icon: LucideIcon;
};

function resolveCategory(pathname: string): CategoryContext | null {
  if (pathname === '/pharmacies' || pathname.startsWith('/pharmacies/') || pathname === '/overview') {
    return { code: 'PHARMACY', label: 'Pharmacies', listPath: '/pharmacies', icon: Pill };
  }
  if (pathname === '/supermarkets' || pathname.startsWith('/supermarkets/')) {
    return { code: 'SUPERMARKET', label: 'Supermarkets', listPath: '/supermarkets', icon: ShoppingCart };
  }
  return null;
}

function NavGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <p className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">{title}</p>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors focus-visible:shadow-focus focus-visible:outline-none',
            isActive ? 'bg-surface-sunken font-semibold text-ink' : 'text-ink-secondary hover:bg-surface-sunken/70 hover:text-ink',
          )
        }
      >
        {({ isActive }) => (
          <>
            <Icon size={16} aria-hidden="true" className={isActive ? 'text-primary' : 'text-ink-muted'} />
            {item.label}
          </>
        )}
      </NavLink>
    </li>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const can = useCan();
  const { pathname } = useLocation();
  const category = resolveCategory(pathname);

  const businessItems = useMemo(() => {
    const items: NavItem[] = [{ to: '/categories', label: 'Categories', icon: LayoutGrid, permission: 'pharmacy.read', end: true }];
    if (category?.code === 'PHARMACY') {
      items.push({ to: '/pharmacies', label: 'Pharmacies', icon: Pill, permission: 'pharmacy.read' });
      if (can('pharmacy.read')) {
        items.push({ to: '/overview', label: 'Overview', icon: LayoutDashboard, permission: 'pharmacy.read' });
      }
    } else if (category?.code === 'SUPERMARKET') {
      items.push({ to: '/supermarkets', label: 'Supermarkets', icon: ShoppingCart, permission: 'business.read' });
    }
    return items.filter((item) => can(item.permission));
  }, [can, category?.code]);

  const operationsItems = useMemo(() => {
    const items: NavItem[] = [
      { to: '/licenses', label: 'License', icon: KeyRound, permission: 'license.read' },
      { to: '/sync', label: 'Sync', icon: Wifi, permission: 'sync.read' },
      { to: '/updates', label: 'Deployment Center', icon: Download, permission: 'update.read' },
      { to: '/activity', label: 'Activity', icon: Activity, permission: 'activity.read' },
      { to: '/production-linking', label: 'Production Linking', icon: Link2, permission: 'production.read' },
      { to: '/settings', label: 'Settings', icon: Settings, permission: 'settings.read' },
    ];
    return items.filter((item) => can(item.permission));
  }, [can]);

  const CategoryIcon = category?.icon ?? LayoutGrid;

  return (
    <div className="flex h-full flex-col">
      <div className="px-3 pb-1 pt-4">
        {category ? (
          <NavLink
            to="/categories"
            onClick={onNavigate}
            className="flex items-center gap-2 rounded-md bg-primary-soft px-2.5 py-2 text-primary transition-colors hover:bg-primary-soft/80 focus-visible:shadow-focus focus-visible:outline-none"
            title="Back to Categories"
          >
            <CategoryIcon size={15} aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{category.label}</span>
            <span className="text-[11px] font-medium text-primary/80">Change</span>
          </NavLink>
        ) : (
          <div className="rounded-md bg-surface-sunken px-2.5 py-2 text-[13px] font-medium text-ink-secondary">
            Central Admin
          </div>
        )}
      </div>

      <nav aria-label="Main navigation" className="flex-1 overflow-y-auto px-3 py-3">
        {businessItems.length > 0 && (
          <NavGroup title="Businesses">
            {businessItems.map((item) => (
              <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </NavGroup>
        )}
        {operationsItems.length > 0 && (
          <NavGroup title="Operations">
            {operationsItems.map((item) => (
              <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
          </NavGroup>
        )}
      </nav>
    </div>
  );
}

/** Main application shell — category-aware, no global Devices/API stubs. */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawerOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-screen">
      <a
        href="#main-content"
        className="sr-only z-toast rounded-md bg-surface px-3 py-2 text-sm font-medium text-primary shadow-overlay focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to content
      </a>

      <header className="fixed inset-x-0 top-0 z-30 flex h-topbar items-center gap-3 border-b border-line bg-surface px-4 lg:px-6">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          className="-ml-1 flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-sunken focus-visible:shadow-focus focus-visible:outline-none lg:hidden"
        >
          <Menu size={20} aria-hidden="true" />
        </button>
        <Brand />
        <div className="ml-auto flex items-center gap-3">
          <EnvironmentBadge />
          <span aria-hidden="true" className="hidden h-6 w-px bg-line sm:block" />
          <UserMenu />
        </div>
      </header>

      <aside className="fixed bottom-0 left-0 top-topbar hidden w-sidebar border-r border-line bg-surface lg:block">
        <SidebarContent />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-drawer lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 animate-fade-in bg-ink/40" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
          <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] animate-fade-in flex-col bg-surface shadow-overlay">
            <div className="flex h-topbar items-center justify-between border-b border-line px-4">
              <Brand />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close navigation"
                autoFocus
                className="flex h-9 w-9 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-sunken focus-visible:shadow-focus focus-visible:outline-none"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <SidebarContent onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="pt-topbar focus:outline-none lg:pl-sidebar">
        <div className="mx-auto max-w-content px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
